import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import Select, { components } from 'react-select';
import { profilesApi } from '../../api/profiles';
import ColumnsIcon from '../../assets/icons/columns.svg';
import { debounce } from '../../utils/debounce';
import { placeholderStyle } from '../AddSegment/Condition/Condition.constants';
import styles from './Contacts.module.scss';
import { useContactsParams } from './useContactsParams';

const COLUMNS_TO_ADD = ['audiences', 'flows', 'port_value', 'buying_power'];

const CustomOption = (props) => {
  const { isSelected, label } = props;
  return (
    <components.Option {...props} className={styles.column_option_wrapper}>
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => {}}
        className={styles.column_option}
      />
      {label}
    </components.Option>
  );
};

const CustomValueContainer = ({ children, ...props }) => {
  const { isFocused, inputValue, placeholder } = props.selectProps;
  const showPlaceholder = !inputValue && !isFocused;

  return (
    <components.ValueContainer {...props}>
      {showPlaceholder && (
        <components.Placeholder {...props}>
          <div data-placeholder="true">{placeholder}</div>
        </components.Placeholder>
      )}
      {children}
    </components.ValueContainer>
  );
};

const ColumnsFilter = ({ isLoading }) => {
  const dispatch = useDispatch();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const { params, updateParam } = useContactsParams();

  const [selectedFields, setSelectedFields] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [displayedOptions, setDisplayedOptions] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  const [isFocused, setIsFocused] = useState(false);

  const buttonRef = useRef(null);
  const selectRef = useRef(null);
  const prevIsDropdownVisibleRef = useRef();

  useEffect(() => {
    if (params.fields) {
      const fieldArray = params.fields.split(',');
      const hasListsOrSegments =
        fieldArray.includes('lists') || fieldArray.includes('segments');
      const result = fieldArray.filter(
        (field) => field !== 'lists' && field !== 'segments'
      );
      if (hasListsOrSegments) result.push('audiences');
      result.sort();
      const tempFields = result.map((field) => ({
        value: field,
        label: field === 'port_value' ? 'portfolio' : field,
      }));

      if (JSON.stringify(selectedFields) !== JSON.stringify(tempFields)) {
        setSelectedFields(tempFields);
      }
    } else {
      setSelectedFields([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.fields]);

  useEffect(() => {
    prevIsDropdownVisibleRef.current = isDropdownVisible;
  });
  const wasDropdownVisible = prevIsDropdownVisibleRef.current;

  useEffect(() => {
    if (!isDropdownVisible) {
      setInputValue('');
      setIsFocused(false);
    }
    if (wasDropdownVisible && !isDropdownVisible) {
      const newFields = selectedFields
        .map((field) => field.value)
        .join(',')
        .replace('audiences', 'lists,segments');
      if (params.fields !== newFields) {
        updateParam('fields', newFields || '');
      }
    }
  }, [
    isDropdownVisible,
    selectedFields,
    params,
    updateParam,
    wasDropdownVisible,
  ]);

  const loadOptions = useCallback(async () => {
    if (!isDropdownVisible) return;
    setIsFetching(true);
    try {
      const apiResult = await dispatch(
        profilesApi.endpoints.getProfileColumns.initiate(inputValue)
      ).unwrap();
      const result = COLUMNS_TO_ADD.filter(
        (v) => !selectedFields.map((r) => r.value).includes(v)
      );
      const columnsToAdd = result.filter((col) =>
        col.toLowerCase().includes(inputValue.toLowerCase())
      );
      const unselectedColumns = [...apiResult.data, ...columnsToAdd].sort();
      const searchedOptions = unselectedColumns.map((item) => ({
        value: item,
        label: item === 'port_value' ? 'portfolio' : item,
      }));
      const combinedOptions = new Map();
      selectedFields.forEach((option) =>
        combinedOptions.set(option.value, option)
      );
      searchedOptions.forEach((option) => {
        if (!combinedOptions.has(option.value))
          combinedOptions.set(option.value, option);
      });
      setDisplayedOptions(Array.from(combinedOptions.values()));
    } catch (error) {
      setDisplayedOptions(selectedFields);
    } finally {
      setIsFetching(false);
    }
  }, [isDropdownVisible, dispatch, inputValue, selectedFields]);

  const debouncedLoadOptions = useMemo(
    () => debounce(loadOptions, 500),
    [loadOptions]
  );

  useEffect(() => {
    debouncedLoadOptions();
  }, [inputValue, selectedFields, debouncedLoadOptions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest('[data-placeholder="true"]')) {
        return;
      }

      const menuEl = selectRef.current?.menuListRef;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        selectRef.current?.controlRef &&
        !selectRef.current.controlRef.contains(event.target) &&
        menuEl &&
        !menuEl.contains(event.target)
      ) {
        setIsDropdownVisible(false);
      }
    };
    if (isDropdownVisible)
      document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownVisible]);

  const customSelectStyles = {
    ...placeholderStyle,
    menu: (base) => ({ ...base, marginTop: '1px' }),
  };

  return (
    <div className={styles.columns_wrapper}>
      <button
        ref={buttonRef}
        className={styles.filter_button}
        type="button"
        onClick={() => setIsDropdownVisible((prev) => !prev)}
      >
        <ColumnsIcon height={20} width={20} />
      </button>
      {isDropdownVisible && (
        <Select
          ref={selectRef}
          options={displayedOptions}
          inputValue={inputValue}
          onInputChange={(newValue, { action }) => {
            if (action === 'input-change') setInputValue(newValue);
          }}
          isLoading={isFetching || isLoading}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          isFocused={isFocused}
          blurInputOnSelect={false}
          closeMenuOnSelect={false}
          styles={customSelectStyles}
          placeholder="Search..."
          menuIsOpen
          maxMenuHeight={400}
          components={{
            DropdownIndicator: null,
            ClearIndicator: null,
            MultiValue: () => null,
            Option: CustomOption,
            ValueContainer: CustomValueContainer,
          }}
          className={`w-100 ${styles.columns_dropdown}`}
          value={selectedFields}
          isSearchable
          onChange={setSelectedFields}
          isMulti
          hideSelectedOptions={false}
          filterOption={null}
        />
      )}
    </div>
  );
};

export default ColumnsFilter;
